import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Space, Spin } from 'antd'
import { isEmpty } from 'lodash'

import { UpdateDataText } from '../../../core/components/sections'

import {
  FormButtons,
  HistoryLink,
  MetadataTree,
} from '../../../common/components/sections'
import {
  StyledPageActions,
  StyledPageHeader,
  StyledPageTitle,
  StyledVerticalSpace,
} from '../../../common/components/styled'
import {
  EVENT_METADATA_TYPES,
  METADATA_TYPES,
  PERMISSIONS,
} from '../../../common/constants'
import { showErrorNotification } from '../../../common/helpers'
import {
  useDocumentTitle,
  useLatestEventByEntityTypesQuery,
  useUserHasPermissions,
} from '../../../common/hooks'

import { renderAllergenEvent } from '../../helpers'
import {
  useAllergensQuery,
  useDeleteAllergenMutation,
  useSaveAllergensMutation,
  useSetIsCurationEnabledAllergenMutation,
} from '../../hooks'
import {
  addAllergen,
  renameAllergen,
  resetAllergenTree,
  setDataForRenamingAllergen,
} from '../../redux/actions'

const AllergensPage = () => {
  const dispatch = useDispatch()
  useDocumentTitle('Allergens')

  const eventEntityTypes = useMemo(() => [EVENT_METADATA_TYPES.ALLERGEN], [])

  const {
    isLoading: isFetchLatestEventLoading,
    error: fetchLatestEventError,
    data: latestEvent,
    refetch: refetchLastEvent,
  } = useLatestEventByEntityTypesQuery(eventEntityTypes)

  const {
    isLoading: isFetchLoading,
    error: fetchError,
    initialData,
  } = useAllergensQuery()

  const [saveAllergens, { isLoading: isSaveLoading, error: saveError }] =
    useSaveAllergensMutation(refetchLastEvent)

  const [deleteAllergen] = useDeleteAllergenMutation(refetchLastEvent)

  const [setIsCurationEnabledAllergen] =
    useSetIsCurationEnabledAllergenMutation(refetchLastEvent)

  const { isDirty, data, dataForRenaming } = useSelector(
    state => state.allergens.allergens,
  )

  const hasFullPermission = useUserHasPermissions(
    PERMISSIONS.METADATA_TREE.SAVE,
  )

  const hasPermissionToViewDetails = useUserHasPermissions(
    PERMISSIONS.METADATA_TREE.GET,
  )

  const treeData = isDirty ? data : initialData

  const handleAdd = useCallback(node => dispatch(addAllergen(node)), [dispatch])

  const handleDelete = useCallback(
    ({ id }) => deleteAllergen(id),
    [deleteAllergen],
  )

  const handleSetIsCurationEnabled = useCallback(
    ({ id, isCurationEnabled }) =>
      setIsCurationEnabledAllergen(id, isCurationEnabled),
    [setIsCurationEnabledAllergen],
  )

  const handleRename = useCallback(
    ({ path, name }) => dispatch(renameAllergen({ path, name })),
    [dispatch],
  )

  const handleSetDataForRenaming = useCallback(
    ({ id }) => dispatch(setDataForRenamingAllergen({ id })),
    [dispatch],
  )

  const handleResetTree = useCallback(
    () => dispatch(resetAllergenTree()),
    [dispatch],
  )

  const handleSaveData = useCallback(
    () => saveAllergens(data),
    [data, saveAllergens],
  )

  useEffect(() => {
    if (saveError?.message || fetchError?.message || fetchLatestEventError) {
      showErrorNotification({
        title: 'Allergens error',
        description:
          saveError?.message || fetchError?.message || fetchLatestEventError,
      })
    }
  }, [fetchError, fetchLatestEventError, saveError])

  return isFetchLoading ||
    isSaveLoading ||
    isFetchLatestEventLoading ||
    isEmpty(treeData) ? (
    <Spin />
  ) : (
    <>
      <StyledPageHeader>
        <StyledPageTitle>Allergens</StyledPageTitle>
        <StyledPageActions>
          <StyledVerticalSpace>
            <Space>
              <UpdateDataText
                updatedAt={latestEvent?.createdAt}
                updatedBy={latestEvent?.createdBy?.name}
              />

              <HistoryLink
                renderEvent={renderAllergenEvent}
                filterEventTypes
                eventMetadataTypes={eventEntityTypes}
              />
            </Space>
            <FormButtons
              permissions={[PERMISSIONS.METADATA_TREE.SAVE]}
              isDisabled={!isDirty}
              onReset={handleResetTree}
              onSave={handleSaveData}
            />
          </StyledVerticalSpace>
        </StyledPageActions>
      </StyledPageHeader>
      <MetadataTree
        disableLink={!hasPermissionToViewDetails}
        metadataType={METADATA_TYPES.ALLERGENS}
        isDirty={isDirty}
        showSearch
        treeData={treeData}
        editable={hasFullPermission}
        addNode={handleAdd}
        deleteNode={handleDelete}
        renameNode={handleRename}
        setDataForRenaming={handleSetDataForRenaming}
        setIsCurationEnabled={handleSetIsCurationEnabled}
        dataForRenaming={dataForRenaming}
      />
    </>
  )
}

export default AllergensPage
